<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
        <h1 class="flex-0 text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
          Surveys
        </h1>
        <nav class="flex items-end gap-4">
                <ViewToggle v-model="tab" :views="viewOptions" />
                <FormButton :to="{ name: 'survey-add', }" prefix-class="fak" icon="fa-plus" color="blue">Add New Survey
                </FormButton>
            </nav>
    </header>
    <div  class="flex flex-col mt-4 p-4 rounded-lg">
        <div v-if="tab === 'list'" class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
            <div class="mb-4 flex-initial">
                <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by
                    Keyword</label>
                <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
            </div>
            <div class="mb-4 flex-initial">
                <SelectMultipleField v-model="query.communities" label="Community" :options="communities.filter(community => community.status != 'inactive').map(community => ( { label: community.meta?.info?.name ?? community.kova?.Name, value: community.id } ))" empty="All Communities" all="All Communities" />
            </div>
            <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
                <FormButton @click="reset" color="blue">Clear Filters</FormButton>
            </div>
            <div class="mb-4 flex-initial sm:mt-6 ml-auto">
                <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
                    <p class="relative">
                        <span class="fak fa-col-options leading-none"></span>
                        <span class="sr-only">Open Table Column Preferences Drawer</span>
                    </p>
                </button>
            </div>
        </div>

        <section v-if="tab === 'list'">
            <h2 class="sr-only">List View</h2>
            <PaginationTable empty-message="There are currently no survey items." v-if="!isLoading" :legacy="false" :data="filtered" :default="5" :show-columns="showColumns" @closeDrawer="showColumns = false" :columns="columns" view="survey-list">

                <template #col-filename="{ row }">
                    <router-link class="block whitespace-nowrap" :to="{ name: 'survey-show', params: { id: row.id } }">
                        <div class="flex items-center gap-2">
                            <div class="flex-shrink-0 text-2xl text-bhav-blue-500">
                                <span class="fas fa-file-xml"></span>
                            </div>
                            <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                                {{ row.filename }}
                            </div>
                        </div>
                    </router-link>
                </template>

                <template #col-community="{ row }">
                    <router-link class="block whitespace-nowrap" :to="{ name: 'survey-show', params: { id: row.id } }">
                        <p class="text-sm leading-5 text-bhav-blue-500">
                            {{ row.community_name }}
                        </p>
                    </router-link>
                </template>

                <template #col-filing="{ row }">
                    <router-link class="block whitespace-nowrap" :to="{ name: 'survey-show', params: { id: row.id } }">
                        <p class="text-sm leading-5 text-bhav-blue-500">
                            {{ row?.meta?.filing }}
                        </p>
                    </router-link>
                </template>

                <template #col-created_at="{ row }">
                    <router-link class="block whitespace-nowrap" :to="{ name: 'survey-show', params: { id: row.id } }">
                        {{ getDate(row.created_at) }}
                    </router-link>
                </template>

                <template #col-actions="{ row }">
                    <FormButton prefix-class="fak" icon="fa-trash" @click="openDelete(row)" color="red"/>
                </template>

            </PaginationTable>
        </section>
        <section v-else>
            <nav class="flex justify-end mt-4">
                <Dropdown 
                    label="Actions"
                    :options="[
                        { 
                            label: 'Download SVG', 
                            click: download, 
                            icon: downloading ? 'fak fa-history' : 'fak fa-download' 
                        }
                    ]"/>
            </nav>
            <MapView v-if="parcels.length" :sources="sources" :layers="layers"
                :center="{ lng: -111.91173865539844, lat: 40.3635719994329 }" :zoom="4" :padding="150" class="mt-8 md:h-[70vh]">
                <template #legend>
                    <dl>
                        <dt class="text-oakwood-gray-500 text-sm font-bold mt-4 sr-only">Key</dt>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Green"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-green-700"></span>
                            Available Home
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Orange"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-orange-500"></span>
                            Model Home
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Blue"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-bhav-blue-500"></span> New
                            Lot
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Blue"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-bhav-blue-700"></span>
                            Available Lot
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Light Amber"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-gold-200"></span> Sold
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Amber"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-gold-400"></span> Sold
                            Contingent
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Red"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-red-500"></span>
                            Closed
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Dark Gray"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-gray-800"></span> Void
                        </dd>
                        <dd class="text-oakwood-gray-900 text-sm flex items-center gap-1">
                            <span title="Light Gray"
                                class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-gray-200"></span>
                            Unassigned
                        </dd>
                    </dl>
                </template>
            </MapView>
        </section>
    </div>

    <ModalDialog v-if="deleting" no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-oakwood-red-600 font-semibold">Confirm Survey Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900 mb-4"><strong>Warning!</strong> You are about to delete <em>{{
                    survey.filename }}</em>.
            </p>
            <p class="mb-4">Deleting this survey will also <strong>delete related parcels</strong> which will also be
                <strong>unlinked from matching lots.</strong></p>
            <p>Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="drop" icon="fa-trash"
                color="red" prefix-class="fak" />
            <FormButton type="button" @click="cancel"
                color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
  </div>
</template>

<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, SelectMultipleField } from '@/components/form';
import ModalDialog from "@/components/ModalDialog.vue";
import PaginationTable from "@/components/PaginationTable.vue";
import MapView from "@/components/MapView.vue";
import colors from "@/helpers/colors";
import SearchInput from "@/components/form/SearchInput.vue";
import ViewToggle from "@/components/ViewToggle.vue";
import Dropdown from "@/components/form/Dropdown.vue";

const query = {
    search: '',
    communities: []
}

export default {
    setup() {
        return {
            columns: [
                {
                    name: 'filename',
                    label: 'File Name',
                    sort: (row) => row.filename || "",
                    default: true
                },
                {
                    name: 'community',
                    label: 'Community',
                    sort: (row) => row.community_name || "",
                    default: true
                },
                {
                    name: 'filing',
                    label: 'Filing',
                    sort: (row) => row.meta?.filing || "",
                    default: true
                },
                {
                    name: 'created_at',
                    label: 'Created Date',
                    sort: (row) => new Date(row.created_at).getTime(),
                    default: true
                },
                {
                    name: 'actions',
                    label: '',
                    sort: null,
                    default: true,
                    show: false
                }
            ]
        }
    },
    data() {
        return {
            tab: "list",
            isLoading: true,
            query: { ...query },
            surveys: [],
            communities: [],
            survey: {},
            deleting: false,
            parcels: [],
            downloading: false,
            showColumns: false,
            viewOptions: [
                { value: 'list', icon: 'fak fa-list-view', label: 'Switch to List View' },
                { value: 'map', icon: 'fak fa-map', label: 'Switch to Map View' }
            ]
        }
    },
    components: {
        LoadingView, FormButton, SelectMultipleField, ModalDialog, PaginationTable, MapView, SearchInput, ViewToggle, Dropdown
    },
    methods: {
        index() {
            this.isLoading = true;
            axios.get('surveys')
                .then(resp => {
                    this.surveys = resp.data;
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        indexCommunities() {
            this.isLoading = true;
            axios.get('divisions/communities')
                .then(resp => {
                    this.communities = resp.data.sort((a, b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        indexParcels() {
            this.isLoading = true;
            axios.get('parcels')
                .then(resp => {
                    this.parcels = resp.data;
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        getCommunityName(id) {
            return this.communities.find((community) => community.id == id)?.meta?.info?.name ?? ''
        },
        getDate(date) {
            return new Intl.DateTimeFormat('en-US', {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
                hour12: true,
                timeZone: "America/Denver",
            }).format(new Date(date));
        },
        reset() {
            this.query = { ...query };
        },
        drop() {
            axios.delete(`surveys/${this.survey.id}`)
                .then(() => {
                    this.index();
                    this.deleting = false;
                })
                .catch(err => {
                    console.error(err);
                })
        },
        cancel() {
            this.survey = {};
            this.deleting = false;
        },
        openDelete(item) {
            this.survey = item;
            this.deleting = true;
        },
        async download() {
            this.downloading = true;
            await axios.get(`parcels/svg`, {headers: {'Accept' : 'image/svg+xml'}, responseType: 'blob'})
                .then((res) => {
                    const file = res.headers['content-disposition'].split('; ')[1].split('=')[1] ?? 'file.svg';
                    // create file link in browser's memory
                    const href = URL.createObjectURL(res.data);

                    // create "a" HTML element with href to file & click
                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', file); //or any other extension
                    document.body.appendChild(link);
                    link.click();

                    // clean up "a" element & remove ObjectURL
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                })
                .catch((err) => { console.error(err) })
                .finally(() => {
                    this.downloading = false;
                })
        }
    },
    computed: {
        hasFilters() {
            return JSON.stringify(this.query) != JSON.stringify(query);
        },
        filtered() {
            let results = this.surveys;

            if (this.query.search !== '') {
                const searchTerm = this.query.search.toLowerCase();
                results = results.filter(item => {
                    const filename = (item.filename || '').toLowerCase();
                    const created_at = (item.created_at ? this.getDate(item.created_at) : '')
                    const filing = (item.meta?.filing || '').toLowerCase();

                    return filename.includes(searchTerm) || created_at.includes(searchTerm) || filing.includes(searchTerm);
                });
            }

            if (this.query.communities.length > 0) {
                results = results.filter(item => this.query.communities.includes(item.division_id));
            }

            return results.map(survey => ({
                ...survey,
                community_name: this.getCommunityName(survey.division_id)
            }));
        },
        sources() {
            return [
                {
                    id: 'lots',
                    name: 'Lot Parcels',
                    features: this.parcels.map(parcel => {
                        const { id, data, lot } = parcel;
                        return {
                            ...data,
                            id,
                            properties: {
                                ...data.properties,
                                lot: lot.lot_number ?? null,
                                block: lot.block_number ?? null,
                                id,
                                sales_order: lot?.sales_order?.status === 'active' ? lot.sales_order : undefined,
                                clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot.kova?.Status),
                                lot_id: lot.id ?? false,
                                status: lot?.kova?.Status ?? null,
                                type: lot?.kova?.Type ?? null
                            }
                        }
                    })
                },
                {
                    id: 'lot_points',
                    name: 'Lot Points',
                    features: this.parcels
                        .map(parcel => {
                            const { id, lot, centroid } = parcel;

                            return {
                                type: "Feature",
                                geometry: {
                                    type: "Point",
                                    coordinates: centroid
                                },
                                properties: {
                                    id,
                                    sales_order: lot?.sales_order?.status === 'active' ? lot.sales_order : undefined,
                                    clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot?.kova?.Status),
                                    name: lot?.kova?.LotID,
                                    lot_rid: lot.lot_rid,
                                    lot_id: lot.id,
                                    status: lot?.kova?.Status ?? null,
                                    street: lot?.kova?.StreetAddress,
                                    city: lot?.kova?.City,
                                    state: lot?.kova?.State,
                                    zip: lot?.kova?.Zip,
                                    type: lot?.kova?.Type ?? null
                                }
                            }
                        }),
                    cluster: true,
                    clusterMaxZoom: 13,
                    clusterRadius: 100
                }
            ]
        },
        layers() {
            return [
                {
                    id: 'lot-base',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-gray'][300],
                            colors['oakwood-gray'][200]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    before: 'lot-new'
                },
                {
                    id: 'lot-new',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][400],
                            colors['bhav-blue'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'New']
                },
                {
                    id: 'lot-available',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][600],
                            colors['bhav-blue'][700]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'Available']
                },
                {
                    id: 'lot-market',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-green'][600],
                            colors['oakwood-green'][700]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'type', 'Market']
                },
                {
                    id: 'lot-model',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-orange'][400],
                            colors['oakwood-orange'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'type', 'Model']
                },
                {
                    id: 'lot-closed',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-red'][400],
                            colors['oakwood-red'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'Closed']
                },
                {
                    id: 'lot-sold',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['gold'][100],
                            colors['gold'][200]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'Sold']
                },
                {
                    id: 'lot-sold-contingent',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['gold'][300],
                            colors['gold'][400]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'SoldContingent']
                },
                {
                    id: 'lot-void',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-gray'][800],
                            colors['oakwood-gray'][900]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: ['==', 'status', 'Void']
                },

                {
                    id: 'lot-boundaries',
                    type: 'line',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'line-color': colors.white,
                        'line-width': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            14, .125,
                            24, 4,
                        ]
                    },
                    before: 'lot-labels'
                },
                {
                    id: 'available-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'available-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            17,
                            [0, 0],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: ['==', 'type', 'Market']
                },
                {
                    id: 'model-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'model-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, 0],
                            24,
                            [0, 30]
                        ],
                    },
                    filter: ['==', 'type', 'Model']
                },
                {
                    id: 'lot-labels',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'text-field': ['coalesce', ['get', 'street'], 'Unassigned'],
                        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                        'text-radial-offset': 0,
                        'text-justify': 'center',
                        'text-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            12,
                            24,
                            16
                        ],
                        'text-max-width': 25
                    },
                    minzoom: 18,
                    paint: {
                        'text-color': colors['oakwood-gray'][900],
                        'text-halo-color': colors['white'],
                        'text-halo-width': 2,
                        'text-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0, -12],
                            24,
                            [0, -30]
                        ],
                        'text-emissive-strength': 2
                    },
                },
                {
                    id: 'clusters',
                    type: 'circle',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': colors['bhav-blue'][700],
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            10,
                            100,
                            15,
                            300,
                            20,
                            500,
                            25
                        ],
                        'circle-emissive-strength': 1,
                        'circle-stroke-color': colors['white'],
                        'circle-stroke-width': 2
                    }
                },
                {
                    id: 'cluster-count',
                    type: 'symbol',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': ['get', 'point_count_abbreviated'],
                        'text-size': 12
                    },
                    paint: {
                        'text-color': colors['white']
                    }
                }
            ]
        }
    },
    created() {
        this.index();
        this.indexCommunities();
        this.indexParcels();
    }
}
</script>